import { useCallback, useEffect, useMemo, useState } from "react";
import { Link } from "react-router-dom";
import PageBreadcrumb from "../../components/common/PageBreadCrumb";
import axiosInstance from "../../hooks/axios";
import { vehicleTypeLabel } from "../../utils/vehicleTypeLabel";

type FleetTab = "standard" | "executive";
type FuelTab = "petrol_diesel_hybrid";
type SortBy = "createdAt" | "pickup";

type VehicleTypeOption = {
  id: string;
  name?: string | null;
  description: string;
  seatCapacity: number | null;
  isMileageBase: boolean;
};

type PapRow = {
  id: string;
  referenceNumber: number;
  fuelCategory: string;
  fleetCategory: string;
  pickupPostcodes: string;
  dropoffPostcodes: string;
  prices: Record<string, number>;
  status: boolean;
};

function vehicleLabel(v: VehicleTypeOption | undefined, description?: string): string {
  if (!v && !description) return "—";
  return vehicleTypeLabel(v ?? { description: description ?? "" });
}

function shortColumnLabel(v: VehicleTypeOption): string {
  return vehicleTypeLabel(v);
}

export default function PostcodeAreaPrices() {
  const [fuelTab] = useState<FuelTab>("petrol_diesel_hybrid");
  const [fleetTab, setFleetTab] = useState<FleetTab>("standard");
  const [rows, setRows] = useState<PapRow[]>([]);
  const [vehicleTypes, setVehicleTypes] = useState<VehicleTypeOption[]>([]);
  const [pickupPostcodes, setPickupPostcodes] = useState("");
  const [dropoffPostcodes, setDropoffPostcodes] = useState("");
  const [prices, setPrices] = useState<Record<string, string>>({});
  const [createReverse, setCreateReverse] = useState(true);
  const [autoUplift, setAutoUplift] = useState(true);
  const [baseVehicleTypeId, setBaseVehicleTypeId] = useState("");
  const [editingId, setEditingId] = useState<string | null>(null);
  const [search, setSearch] = useState("");
  const [sortBy, setSortBy] = useState<SortBy>("createdAt");
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [message, setMessage] = useState("");

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const [pricesRes, typesRes] = await Promise.all([
        axiosInstance.get<PapRow[]>("/postcode-area-prices", {
          params: {
            fleetCategory: fleetTab,
            fuelCategory: fuelTab,
            search: search.trim() || undefined,
            sortBy,
          },
        }),
        axiosInstance.get("/postcode-area-prices/vehicle-types", {
          params: { fleetCategory: fleetTab },
        }),
      ]);
      setRows(pricesRes.data);
      setVehicleTypes(typesRes.data);
      const base =
        typesRes.data.find((v: VehicleTypeOption) => v.isMileageBase) ??
        typesRes.data.find((v: VehicleTypeOption) => (v.seatCapacity ?? 99) <= 4);
      const baseId = base?.id ?? typesRes.data[0]?.id ?? "";
      setBaseVehicleTypeId((prev) => prev || baseId);
      setPrices((prev) => {
        const next = { ...prev };
        for (const v of typesRes.data as VehicleTypeOption[]) {
          if (next[v.id] === undefined) next[v.id] = "";
        }
        return next;
      });
    } catch {
      setMessage("Failed to load postcode area prices");
    } finally {
      setLoading(false);
    }
  }, [fleetTab, fuelTab, search, sortBy]);

  useEffect(() => {
    load();
  }, [load]);

  const resetForm = () => {
    setPickupPostcodes("");
    setDropoffPostcodes("");
    setPrices({});
    setCreateReverse(true);
    setAutoUplift(true);
    setEditingId(null);
    const base =
      vehicleTypes.find((v) => v.isMileageBase) ??
      vehicleTypes.find((v) => (v.seatCapacity ?? 99) <= 4);
    setBaseVehicleTypeId(base?.id ?? vehicleTypes[0]?.id ?? "");
    for (const v of vehicleTypes) {
      setPrices((p) => ({ ...p, [v.id]: "" }));
    }
  };

  const buildPricesPayload = (): Record<string, number> => {
    const out: Record<string, number> = {};
    for (const [id, val] of Object.entries(prices)) {
      const n = Number(val);
      if (!Number.isNaN(n) && n > 0) out[id] = n;
    }
    return out;
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setSaving(true);
    setMessage("");
    try {
      const pricesPayload = buildPricesPayload();
      const payload = {
        fuelCategory: fuelTab,
        fleetCategory: fleetTab,
        pickupPostcodes,
        dropoffPostcodes,
        prices: pricesPayload,
        baseVehicleTypeId,
        autoUpliftOtherVehicles: autoUplift,
        createReverse: editingId ? false : createReverse,
        status: true,
      };

      if (editingId) {
        await axiosInstance.put(`/postcode-area-prices/${editingId}`, payload);
        setMessage("Postcode area price updated");
      } else {
        await axiosInstance.post("/postcode-area-prices", payload);
        setMessage("Postcode area price(s) added");
      }
      resetForm();
      await load();
    } catch (err: unknown) {
      const msg =
        (err as { response?: { data?: { message?: string } } })?.response?.data
          ?.message ?? "Save failed";
      setMessage(msg);
    } finally {
      setSaving(false);
    }
  };

  const startEdit = (row: PapRow) => {
    setEditingId(row.id);
    setPickupPostcodes(row.pickupPostcodes);
    setDropoffPostcodes(row.dropoffPostcodes);
    const next: Record<string, string> = {};
    for (const v of vehicleTypes) {
      const p = row.prices[v.id];
      next[v.id] = p != null ? String(p) : "";
    }
    setPrices(next);
    setCreateReverse(false);
    setAutoUplift(false);
    window.scrollTo({ top: 0, behavior: "smooth" });
  };

  const handleDelete = async (id: string) => {
    if (!window.confirm("Delete this postcode area price?")) return;
    await axiosInstance.delete(`/postcode-area-prices/${id}`);
    await load();
  };

  const baseVehicle = vehicleTypes.find((v) => v.id === baseVehicleTypeId);

  const displayRows = useMemo(() => rows, [rows]);

  return (
    <div className="space-y-6 max-w-[90rem]">
      <div className="flex flex-wrap items-start justify-between gap-4">
        <PageBreadcrumb pageTitle="Postcode Area Prices (PAPs)" />
        <div className="flex gap-3 text-sm">
          <Link to="/vehicle-type" className="text-brand-600 hover:text-brand-700 hover:underline dark:text-brand-400">
            Add Fleet Type
          </Link>
          <span className="text-gray-300">|</span>
          <a
            href="https://www.gov.uk/find-postcode"
            target="_blank"
            rel="noreferrer"
            className="text-brand-600 hover:text-brand-700 hover:underline dark:text-brand-400"
          >
            Help
          </a>
        </div>
      </div>

      <p className="mt-4 text-sm text-gray-600 max-w-4xl">
        Set fixed prices between UK postcode areas (outward codes such as SL1 or NW5). You can
        map one area to one, one to many, or many to many. Postcode area prices take priority
        over location prices, per-mile prices, and minimum fares for the same route.
      </p>

      <div className="mt-4 flex gap-2 border-b">
        <button
          type="button"
          className="px-4 py-2 text-sm font-medium border-b-2 -mb-px border-brand-500 text-brand-600 dark:text-brand-400"
        >
          Petrol, Diesel &amp; Hybrid
        </button>
      </div>

      <div className="mt-2 flex gap-2 border-b">
        {(["standard", "executive"] as FleetTab[]).map((tab) => (
          <button
            key={tab}
            type="button"
            onClick={() => setFleetTab(tab)}
            className={`px-4 py-2 text-sm font-medium border-b-2 -mb-px capitalize ${
              fleetTab === tab
                ? "border-brand-500 text-brand-600 dark:text-brand-400"
                : "border-transparent text-gray-500"
            }`}
          >
            {tab}
          </button>
        ))}
      </div>

      <section className="mt-6 rounded-2xl border border-gray-200 bg-white p-5 shadow-theme-sm dark:border-gray-800 dark:bg-white/[0.03] lg:p-6">
        <h2 className="text-lg font-semibold text-gray-800">
          {editingId ? "Edit postcode area" : "Add a postcode area"}
        </h2>
        {!editingId && (
          <p className="text-sm text-gray-500 mt-1">
            Enter prices for each vehicle size, or fill the base 4-seater and use automatic
            uplifts.
          </p>
        )}

        <form onSubmit={handleSubmit} className="mt-4 space-y-4">
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Pickup (postcodes)
              </label>
              <input
                className="w-full border rounded-lg px-3 py-2 uppercase"
                placeholder="e.g. SL1, NW5"
                value={pickupPostcodes}
                onChange={(e) => setPickupPostcodes(e.target.value)}
                required
              />
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Dropoff (postcodes)
              </label>
              <input
                className="w-full border rounded-lg px-3 py-2 uppercase"
                placeholder="e.g. CM24, SO14"
                value={dropoffPostcodes}
                onChange={(e) => setDropoffPostcodes(e.target.value)}
                required
              />
            </div>
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">
              Prices by vehicle (£)
            </label>
            <div className="grid grid-cols-2 sm:grid-cols-4 lg:grid-cols-8 gap-3">
              {vehicleTypes.map((v) => (
                <div key={v.id}>
                  <label className="block text-xs text-gray-500 mb-1 truncate" title={vehicleLabel(v)}>
                    {shortColumnLabel(v)}
                  </label>
                  <div className="flex items-center gap-1">
                    <span className="text-gray-400 text-sm">£</span>
                    <input
                      type="number"
                      min="0"
                      step="0.01"
                      className="w-full border rounded-lg px-2 py-1.5 text-sm"
                      value={prices[v.id] ?? ""}
                      onChange={(e) =>
                        setPrices((p) => ({ ...p, [v.id]: e.target.value }))
                      }
                    />
                  </div>
                </div>
              ))}
            </div>
          </div>

          {!editingId && (
            <div className="flex flex-col gap-2 text-sm">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Base vehicle for auto-uplift
                </label>
                <select
                  className="border rounded-lg px-3 py-2 max-w-xs"
                  value={baseVehicleTypeId}
                  onChange={(e) => setBaseVehicleTypeId(e.target.value)}
                >
                  {vehicleTypes.map((v) => (
                    <option key={v.id} value={v.id}>
                      {vehicleLabel(v)}
                    </option>
                  ))}
                </select>
              </div>
              <label className="flex items-center gap-2">
                <input
                  type="checkbox"
                  checked={createReverse}
                  onChange={(e) => setCreateReverse(e.target.checked)}
                />
                Also create a price for the reverse direction
              </label>
              {baseVehicle &&
                (baseVehicle.isMileageBase || (baseVehicle.seatCapacity ?? 99) <= 4) && (
                  <label className="flex items-center gap-2">
                    <input
                      type="checkbox"
                      checked={autoUplift}
                      onChange={(e) => setAutoUplift(e.target.checked)}
                    />
                    Fill empty vehicle prices from base using uplifts (Per Mile Prices)
                  </label>
                )}
            </div>
          )}

          <div className="flex gap-3">
            <button
              type="submit"
              disabled={saving}
              className="px-5 py-2 bg-brand-500 text-white rounded-lg font-medium disabled:opacity-50"
            >
              {saving ? "Saving..." : editingId ? "Update" : "+ Add"}
            </button>
            {editingId && (
              <button
                type="button"
                onClick={resetForm}
                className="px-4 py-2 border rounded-lg text-gray-700"
              >
                Cancel
              </button>
            )}
          </div>
        </form>
        {message && (
          <p
            className={`mt-3 text-sm ${
              message.includes("Failed") || message.includes("failed")
                ? "text-red-600"
                : "text-green-600"
            }`}
          >
            {message}
          </p>
        )}
      </section>

      <section className="mt-8 overflow-hidden rounded-2xl border border-gray-200 bg-white shadow-theme-sm dark:border-gray-800 dark:bg-white/[0.03]">
        <div className="px-6 pt-6 pb-3 flex flex-wrap gap-4 items-center justify-between">
          <h2 className="text-lg font-semibold">Postcode areas</h2>
          <div className="flex flex-wrap gap-3">
            <input
              type="search"
              placeholder="Search by postcode or part postcode"
              className="border rounded-lg px-3 py-2 text-sm min-w-[220px]"
              value={search}
              onChange={(e) => setSearch(e.target.value)}
            />
            <select
              className="border rounded-lg px-3 py-2 text-sm"
              value={sortBy}
              onChange={(e) => setSortBy(e.target.value as SortBy)}
            >
              <option value="createdAt">Sort by creation date</option>
              <option value="pickup">Sort by pickup area</option>
            </select>
          </div>
        </div>

        {loading ? (
          <p className="px-6 py-8 text-gray-500">Loading...</p>
        ) : (
          <div className="overflow-x-auto">
            <table className="min-w-full text-sm">
              <thead className="bg-gray-50 border-b">
                <tr>
                  <th className="px-3 py-2 text-left whitespace-nowrap">Pickup</th>
                  <th className="px-3 py-2 text-left whitespace-nowrap">Drop-off</th>
                  {vehicleTypes.map((v) => (
                    <th
                      key={v.id}
                      className="px-2 py-2 text-left whitespace-nowrap text-xs"
                      title={vehicleLabel(v)}
                    >
                      {shortColumnLabel(v)}
                    </th>
                  ))}
                  <th className="px-3 py-2 text-right">Actions</th>
                </tr>
              </thead>
              <tbody>
                {displayRows.length === 0 ? (
                  <tr>
                    <td
                      colSpan={vehicleTypes.length + 3}
                      className="px-4 py-8 text-center text-gray-500"
                    >
                      No postcode area prices for {fleetTab} fleet
                    </td>
                  </tr>
                ) : (
                  displayRows.map((row) => (
                    <tr key={row.id} className="border-b hover:bg-gray-50">
                      <td className="px-3 py-3 font-mono text-xs uppercase">
                        {row.pickupPostcodes}
                      </td>
                      <td className="px-3 py-3 font-mono text-xs uppercase">
                        {row.dropoffPostcodes}
                      </td>
                      {vehicleTypes.map((v) => (
                        <td key={v.id} className="px-2 py-3 whitespace-nowrap">
                          {row.prices[v.id] != null
                            ? `£${Number(row.prices[v.id]).toFixed(2)}`
                            : "—"}
                        </td>
                      ))}
                      <td className="px-3 py-3 text-right space-x-3 whitespace-nowrap">
                      <div className="flex items-center justify-end gap-3">
                        <button
                          type="button"
                          className="text-brand-600 hover:text-brand-700 hover:underline dark:text-brand-400"
                          onClick={() => startEdit(row)}
                        >
                          Edit
                        </button>
                        <button
                          type="button"
                          className="text-red-600 hover:underline"
                          onClick={() => handleDelete(row.id)}
                        >
                          Delete
                        </button>
                      </div>
                    </td>
                    </tr>
                  ))
                )}
              </tbody>
            </table>
          </div>
        )}
      </section>
    </div>
  );
}
